import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaBolt,
  FaBuilding,
  FaCalendarAlt,
  FaChartLine,
  FaInbox,
  FaLayerGroup,
  FaSearch,
  FaSignal,
  FaSlidersH,
  FaSync,
  FaUserCheck,
  FaUsers,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { KpiCard } from "./components/KpiCard";
import { DateRangeFilter } from "./components/DateRangeFilter";
import { PartialDataBanner } from "./components/PartialDataBanner";
import { HealthBadge } from "./components/HealthBadge";
import { Avatar } from "./components/Avatar";
import { EnvToggle } from "./components/EnvToggle";
import { useEnv } from "./useEnv";
import { dateRangeParams, fetchUsageOverview } from "./api";
import "./OrgUsageAnalytics.css";

const formatRelative = (ms) => {
  if (!ms) return "—";
  const diff = Date.now() - ms;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  try {
    return new Date(ms).toLocaleDateString();
  } catch {
    return "—";
  }
};

const fmtN = (n) =>
  typeof n === "number" ? n.toLocaleString() : n ?? "0";

const clampPct = (value) => Math.min(100, Math.max(0, Number(value) || 0));

function progressColor(rate) {
  if (rate >= 50) return "ou-progress-fill--green";
  if (rate >= 20) return "ou-progress-fill--amber";
  return "ou-progress-fill--gray";
}

function SkeletonKpis() {
  return (
    <div className="ou-kpi-grid">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="ou-kpi">
          <div className="ou-skel ou-skel-line" style={{ width: "55%" }} />
          <div
            className="ou-skel"
            style={{ height: 28, width: "45%", margin: "10px 0 6px" }}
          />
          <div className="ou-skel ou-skel-line" style={{ width: "40%" }} />
        </div>
      ))}
    </div>
  );
}

function SkeletonOrgGrid() {
  return (
    <div className="ou-org-grid">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="ou-org-card" style={{ cursor: "default" }}>
          <div className="ou-org-head">
            <div className="ou-skel" style={{ width: 40, height: 40 }} />
            <div style={{ flex: 1 }}>
              <div className="ou-skel ou-skel-line" style={{ width: "70%" }} />
              <div className="ou-skel ou-skel-line" style={{ width: "45%" }} />
            </div>
          </div>
          <div className="ou-skel" style={{ height: 60 }} />
        </div>
      ))}
    </div>
  );
}

export default function OrgUsageIndexPage() {
  const [env, setEnv] = useEnv();
  const [days, setDays] = useState(7);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sort, setSort] = useState("most_active");
  const [activeOnly, setActiveOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...dateRangeParams(days),
        search: debouncedSearch,
        sort,
        activeOnly: activeOnly ? "true" : "false",
        limit: "100",
        offset: "0",
      };
      const json = await fetchUsageOverview(params, env);
      setData(json);
    } catch (err) {
      toast.error(err.message || "Failed to load usage overview");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days, debouncedSearch, sort, activeOnly, env]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = data?.globalTotals;
  const orgs = data?.orgs || [];
  const visibleOrgCount = orgs.length;
  const activeOrgRate =
    totals?.totalOrgs > 0
      ? Math.round((totals.orgsWithActivity / totals.totalOrgs) * 100)
      : 0;
  const strongAdoptionCount = orgs.filter(
    (org) => (org.adoptionRate || 0) >= 50,
  ).length;
  const inactiveOrgCount = orgs.filter(
    (org) => !(org.uniqueActiveUsers || 0),
  ).length;
  const topOrg = orgs.reduce((best, org) => {
    if (!best) return org;
    return (org.totalEvents || 0) > (best.totalEvents || 0) ? org : best;
  }, null);

  return (
    <div className="ou-page">
      <ToastContainer position="top-right" autoClose={4000} />

      <header className="ou-hero">
        <div className="ou-hero-copy">
          <div className="ou-eyebrow">
            <FaSignal />
            Usage analytics
          </div>
          <h1 className="ou-title">Users Usage</h1>
          <p className="ou-subtitle">
            Adoption, engagement, and loan activity across every organization.
          </p>
          <div className="ou-hero-chip-row">
            <span className="ou-hero-chip">
              <FaCalendarAlt />
              Last {days} days
            </span>
            <span className="ou-hero-chip">
              <FaBuilding />
              {fmtN(visibleOrgCount)} orgs loaded
            </span>
            {totals ? (
              <span className="ou-hero-chip">
                <FaBolt />
                {fmtN(totals.totalEvents)} events
              </span>
            ) : null}
          </div>
        </div>
        <div className="ou-hero-actions">
          <EnvToggle env={env} onChange={setEnv} />
          <DateRangeFilter days={days} onChange={setDays} />
          <button
            type="button"
            className="ou-btn ou-btn--solid"
            onClick={load}
            disabled={loading}
          >
            <FaSync className={loading ? "fa-spin" : ""} />
            Refresh
          </button>
        </div>
      </header>

      <PartialDataBanner show={data?.partial} />

      {loading && !totals ? (
        <SkeletonKpis />
      ) : totals ? (
        <div className="ou-kpi-grid">
          <KpiCard
            label="Organizations"
            value={fmtN(totals.totalOrgs)}
            icon={<FaBuilding />}
            accent="indigo"
            subtext="Total tenants in scope"
          />
          <KpiCard
            label="With activity"
            value={fmtN(totals.orgsWithActivity)}
            icon={<FaBolt />}
            accent="green"
            subtext={`${
              totals.totalOrgs
                ? Math.round((totals.orgsWithActivity / totals.totalOrgs) * 100)
                : 0
            }% of orgs`}
          />
          <KpiCard
            label="Active users"
            value={fmtN(totals.totalUniqueUsersActive)}
            icon={<FaUserCheck />}
            accent="cyan"
            subtext="Unique users in period"
          />
          <KpiCard
            label="Total events"
            value={fmtN(totals.totalEvents)}
            icon={<FaChartLine />}
            accent="pink"
            subtext="Genuine user events (excl. autoruns)"
          />
          <KpiCard
            label="Avg adoption"
            value={`${totals.avgAdoptionRate ?? 0}%`}
            icon={<FaUsers />}
            accent="amber"
            subtext="Active / total members"
          />
        </div>
      ) : null}

      {totals ? (
        <section className="ou-insight-grid" aria-label="Usage summary">
          <div className="ou-insight ou-insight--wide">
            <div className="ou-insight-top">
              <span>
                <FaLayerGroup />
                Organization coverage
              </span>
              <strong>{activeOrgRate}%</strong>
            </div>
            <div className="ou-progress ou-progress--thick">
              <div
                className="ou-progress-fill ou-progress-fill--green"
                style={{ width: `${clampPct(activeOrgRate)}%` }}
              />
            </div>
            <div className="ou-insight-meta">
              {fmtN(totals.orgsWithActivity)} active of {fmtN(totals.totalOrgs)}{" "}
              organizations
            </div>
          </div>
          <div className="ou-insight">
            <div className="ou-insight-label">Strong adoption</div>
            <div className="ou-insight-value">{fmtN(strongAdoptionCount)}</div>
            <div className="ou-insight-meta">Visible orgs at 50%+ adoption</div>
          </div>
          <div className="ou-insight">
            <div className="ou-insight-label">Needs follow-up</div>
            <div className="ou-insight-value">{fmtN(inactiveOrgCount)}</div>
            <div className="ou-insight-meta">Visible orgs with no activity</div>
          </div>
          <div className="ou-insight">
            <div className="ou-insight-label">Most active org</div>
            <div className="ou-insight-value ou-insight-value--text">
              {topOrg?.organizationName || "--"}
            </div>
            <div className="ou-insight-meta">
              {topOrg ? `${fmtN(topOrg.totalEvents)} events` : "No activity yet"}
            </div>
          </div>
        </section>
      ) : null}

      <div className="ou-filterbar">
        <div className="ou-filterbar-title">
          <span>
            <FaSlidersH />
          </span>
          <div>
            <strong>Organization directory</strong>
            <small>{loading ? "Refreshing results" : `${fmtN(visibleOrgCount)} results`}</small>
          </div>
        </div>
        <div className="ou-search">
          <FaSearch className="ou-search-icon" />
          <input
            placeholder="Search by org name or ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="ou-select"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
        >
          <option value="most_active">Most active</option>
          <option value="least_active">Least active</option>
          <option value="most_members">Most members</option>
          <option value="name">Name A–Z</option>
        </select>
        <label className="ou-toggle-label">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
          />
          Active in period only
        </label>
      </div>

      {loading ? (
        <SkeletonOrgGrid />
      ) : !orgs.length ? (
        <div className="ou-empty">
          <div className="ou-empty-icon">
            <FaInbox />
          </div>
          <div className="ou-empty-title">No organizations match</div>
          <div className="ou-empty-sub">
            Try clearing filters or expanding the date range.
          </div>
        </div>
      ) : (
        <div className="ou-org-grid">
          {orgs.map((org) => {
            const adoptionRate = clampPct(org.adoptionRate);

            return (
              <Link
                key={org.organizationId}
                to={{
                  pathname: `/org-usage-analytics/${org.organizationId}`,
                  state: { organizationName: org.organizationName },
                }}
                className={`ou-org-card ou-org-card--${org.health || "low"}`}
                style={{ "--ou-ring-pct": adoptionRate }}
              >
              <div className="ou-org-card-top">
                <div className="ou-org-head">
                  <Avatar name={org.organizationName} seed={org.organizationId} />
                  <div className="ou-org-identity">
                    <h2 className="ou-org-name" title={org.organizationName}>
                      {org.organizationName}
                    </h2>
                    <div className="ou-org-id" title={org.organizationId}>
                      {org.organizationId}
                    </div>
                  </div>
                </div>
                <HealthBadge health={org.health} />
              </div>

              <div className="ou-org-card-mid">
                <div className="ou-org-adoption">
                  <div className="ou-org-adopt-row">
                    <span>
                      <strong>{fmtN(org.uniqueActiveUsers)}</strong> of{" "}
                      {fmtN(org.totalMembers)} active
                    </span>
                    <strong>{adoptionRate}%</strong>
                  </div>
                  <div className="ou-progress ou-progress--thick">
                    <div
                      className={`ou-progress-fill ${progressColor(org.adoptionRate)}`}
                      style={{ width: `${Math.max(2, adoptionRate)}%` }}
                    />
                  </div>
                </div>
                <div className="ou-org-ring" aria-label={`${adoptionRate}% adoption`}>
                  <span>{adoptionRate}%</span>
                  <small>adoption</small>
                </div>
              </div>

              <div className="ou-org-stats">
                <div>
                  <div className="ou-org-stat-num">{fmtN(org.totalEvents)}</div>
                  <div className="ou-org-stat-lbl">Events</div>
                </div>
                <div>
                  <div className="ou-org-stat-num">{fmtN(org.loansTouched)}</div>
                  <div className="ou-org-stat-lbl" title="Distinct loans opened/viewed">
                    Loans opened
                  </div>
                </div>
                <div>
                  <div className="ou-org-stat-num">{fmtN(org.totalMembers)}</div>
                  <div className="ou-org-stat-lbl">Members</div>
                </div>
              </div>

              <div className="ou-org-meta">
                <span className="ou-feature-chip" title={org.topFeature}>
                  {org.topFeature ? (
                    <>
                      Top <strong>{org.topFeature}</strong>
                    </>
                  ) : (
                    "No activity"
                  )}
                </span>
                <span>{formatRelative(org.lastActivityAt)}</span>
              </div>
              <span className="ou-card-arrow">
                <FaArrowRight />
              </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
